import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import dayjs from 'dayjs';

import type { ISnippet } from 'src/services/api/snippets/types';
import { useCreateActivity } from 'src/services/api/activities/use-create-activity';
import { useSendMessage } from 'src/services/api/messages/use-send-message';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import { getFormattedSignature } from 'src/pages/landlord/leads/messages/utils/format-signature';
import { ComposerContext } from 'src/pages/landlord/leads/messages/context/composer-context';
import { decideComposerSendCommand } from 'src/pages/landlord/leads/messages/context/composer-send';
import {
    composerReducer,
    composerText,
    createInitialComposerState,
    isComposerEmpty,
    isComposerSendable,
    isSignaturePresent,
    makeSourceKey,
    type ComposerAction,
} from 'src/pages/landlord/leads/messages/context/composer-state';

type Props = {
    children: React.ReactNode;
};

/**
 * Dedicated React boundary for the composer document. It gathers the formatted
 * signature source and lead/conversation identity from the outer page provider, exposes only
 * business operations, and consumes the document when a send starts. Mount it with `key={lead.uuid}` so
 * opening another conversation remounts a fresh document instead of relying on a reset effect.
 */
export default function ComposerProvider({ children }: Props) {
    const {
        lead,
        channel,
        userSettings,
        detectedLeadLang,
        isTranslateOutboundEnabled,
    } = useLeadMessagesPageContext();

    const { mutateAsync: sendMessage, isLoading: isSendingMessage } = useSendMessage();
    const { mutateAsync: createActivity } = useCreateActivity();

    // The send mutation targets leadUuid and the provider is keyed by it, so each composer
    // owns exactly one conversation's document.
    const conversationId = lead.uuid;
    const managedSignature = getFormattedSignature(channel, userSettings);
    const sourceKey = makeSourceKey(channel, managedSignature);

    // Lazy initializer: because the provider remounts per lead.uuid, this is always a fresh
    // document seeded from the current signature source.
    const [state, dispatch] = useReducer(
        composerReducer,
        { conversationId, sourceKey, managedSignature },
        createInitialComposerState,
    );
    const stateRef = useRef(state);
    const dispatchComposer = useCallback((action: ComposerAction) => {
        const nextState = composerReducer(stateRef.current, action);
        stateRef.current = nextState;
        dispatch(action);
    }, []);

    // One idempotent effect: when the formatted signature source changes (channel switch or a
    // settings update), replace the tracked region — without touching body or conversation.
    // The ref guard means Strict Mode's double-invoke, and renders where sourceKey is
    // unchanged, never dispatch twice.
    const lastSourceKeyRef = useRef(sourceKey);
    useEffect(() => {
        if (lastSourceKeyRef.current === sourceKey) {
            return;
        }
        lastSourceKeyRef.current = sourceKey;
        dispatchComposer({ type: 'signatureSourceChanged', sourceKey, managedSignature });
    }, [sourceKey, managedSignature, dispatchComposer]);

    const editText = useCallback((text: string) => {
        dispatchComposer({ type: 'textEdited', text });
    }, [dispatchComposer]);

    const applySnippet = useCallback((snippet: ISnippet, seededBody: string) => {
        dispatchComposer({ type: 'snippetApplied', snippetId: snippet.id, body: seededBody });
    }, [dispatchComposer]);

    const acceptSuggestion = useCallback((content: string) => {
        dispatchComposer({ type: 'suggestionAccepted', content });
    }, [dispatchComposer]);

    const insertText = useCallback((text: string) => {
        dispatchComposer({ type: 'textInserted', text });
    }, [dispatchComposer]);

    const toggleSignature = useCallback(() => {
        dispatchComposer({ type: 'signatureToggled', sourceKey, managedSignature });
    }, [sourceKey, managedSignature, dispatchComposer]);

    const sendCurrentMessage = useCallback(async (files?: File[]) => {
        // Gather the current data, then let the pure decision function capture one command.
        const command = decideComposerSendCommand({
            state: stateRef.current,
            channel,
            files,
            detectedLeadLang,
            isTranslateOutboundEnabled,
        });

        // Consume the captured document before starting IO. Success and failure leave any
        // newer document changes alone.
        dispatchComposer({ type: 'sendStarted', sourceKey, managedSignature });

        await sendMessage({
            leadUuid: command.conversationId,
            channel: command.channel,
            message: command.message,
            attachments: command.attachments,
            snippet_id: command.snippetId,
            translate_to_lang: command.translateToLang,
        });

        createActivity({ time: dayjs().format(), type: 'manual_message' }).catch(() => {
            console.error('Failed to create activity');
        });
    }, [
        channel,
        detectedLeadLang,
        isTranslateOutboundEnabled,
        sourceKey,
        managedSignature,
        sendMessage,
        createActivity,
        dispatchComposer,
    ]);

    const value = useMemo(() => ({
        text: composerText(state),
        isEmpty: isComposerEmpty(state),
        canSend: isComposerSendable(state),
        isSignaturePresent: isSignaturePresent(state),
        isSendingMessage,
        editText,
        applySnippet,
        acceptSuggestion,
        insertText,
        toggleSignature,
        sendCurrentMessage,
    }), [state, isSendingMessage, editText, applySnippet, acceptSuggestion, insertText, toggleSignature, sendCurrentMessage]);

    return (
        <ComposerContext.Provider value={value}>
            {children}
        </ComposerContext.Provider>
    );
}
